import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectTenantCurrency, selectTenantCurrencyFormat } from '../store/slices/tenantSlice';
import {
  chargeFieldLabel,
  formatCurrency,
  formatCurrencyCompact,
  formatCurrencyWhole,
  formatMoney,
  getCurrencySymbol,
  normalizeCurrencyCode,
  setActiveCurrency,
  setActiveCurrencyFormat,
  type FormatMoneyArg,
} from '../utils/currency';

/** Tenant-scoped currency formatters for dashboard and admin UI. */
export function useTenantCurrency() {
  const currency = useSelector(selectTenantCurrency);
  const currencyFormat = useSelector(selectTenantCurrencyFormat);
  const code = useMemo(() => normalizeCurrencyCode(currency), [currency]);
  // Changes whenever the Currencies master entry changes, so memoised formatters refresh.
  const formatKey = currencyFormat ? JSON.stringify(currencyFormat) : '';

  useEffect(() => {
    setActiveCurrency(currency || code || '');
    setActiveCurrencyFormat(currencyFormat ?? null);
  }, [currency, code, currencyFormat]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const symbol = useMemo(() => getCurrencySymbol(code || currency), [code, currency, formatKey]);

  const formatAmount = useCallback(
    (amount: number, from?: unknown) => (
      from != null
        ? formatMoney(amount, { from, currency: code || currency })
        : formatMoney(amount, code || currency || undefined)
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, currency, formatKey],
  );

  const formatWhole = useCallback(
    (amount: number) => formatCurrencyWhole(amount, code || undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, formatKey],
  );

  const formatCompact = useCallback(
    (amount: number) => formatCurrencyCompact(amount, code || undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, formatKey],
  );

  const formatIntl = useCallback(
    (amount: number) => formatCurrency(amount, code || undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, formatKey],
  );

  const fieldLabel = useCallback(
    (label: string) => chargeFieldLabel(label, code || currency || undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, currency, formatKey],
  );

  const money = useCallback(
    (amount: unknown, currencyOrOpts?: FormatMoneyArg) => (
      currencyOrOpts != null
        ? formatMoney(amount, currencyOrOpts)
        : formatMoney(amount, code || currency || undefined)
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [code, currency, formatKey],
  );

  return {
    currency: code || currency || '',
    symbol,
    formatAmount,
    formatWhole,
    formatCompact,
    formatIntl,
    fieldLabel,
    formatMoney: money,
  };
}
